/**
 * DashboardQuery → rows, the way the prototype's DashboardProvider and module
 * views sliced them: range → sentiment corrections → chip filter → scope.
 */
import { orderRange, startOfDay } from '@deepdive-ui';

import {
  DIMENSION_KEYS,
  type DashboardQuery,
  type DateRangeValue,
  type Selections,
} from '@/data-sources/contract';
import { fromIsoDate } from '@/lib/analytics/dates';

import { USER_BRAND } from './engine/brand';
import { filterRows, toQuery } from './engine/filter';
import { CORPUS_START, type MentionRow } from './engine/generator';
import { REFERENCE_DATE } from './engine/reference';
import { rowsInRange } from './engine/rowIndex';
import { rowIndex } from './engine/rows';
import { applySentimentOverrides } from './engine/sentimentOverrides';
import { db } from './db';

export type Bounds = { from: number; to: number };

/** The prototype's `rangeBounds`: open start → corpus start, open end → today. */
export function boundsOf(range: DateRangeValue): Bounds {
  const ordered = orderRange({
    start: range.start ? fromIsoDate(range.start) : null,
    end: range.end ? fromIsoDate(range.end) : null,
  });
  return {
    from: (ordered.start ? startOfDay(ordered.start) : CORPUS_START).getTime(),
    to: (ordered.end ? startOfDay(ordered.end) : startOfDay(REFERENCE_DATE)).getTime(),
  };
}

const isOpen = (range: DateRangeValue) => !range.start && !range.end;

export type Sliced = {
  from: number;
  to: number;
  current: readonly MentionRow[];
  previous: readonly MentionRow[] | null;
  previousBounds: Bounds | null;
};

/** Range-sliced, sentiment-corrected rows for every brand, plus the comparison slice. */
export function slice(query: DashboardQuery): Sliced {
  const { from, to } = boundsOf(query.range);
  const overrides = db.sentimentOverrides;
  const current = isOpen(query.range)
    ? []
    : applySentimentOverrides(rowsInRange(rowIndex, from, to), overrides);
  const previousBounds = query.compare ? boundsOf(query.compare) : null;
  const previous = previousBounds
    ? applySentimentOverrides(rowsInRange(rowIndex, previousBounds.from, previousBounds.to), overrides)
    : null;
  return { from, to, current, previous, previousBounds };
}

/** Apply chip selections. The query was already pruned to the page's chips. */
export function applyChips(rows: readonly MentionRow[], selections: Selections): MentionRow[] {
  return filterRows(rows, toQuery(selections, DIMENSION_KEYS));
}

export const own = (rows: readonly MentionRow[]) => rows.filter((row) => row.brand === USER_BRAND);

/** The two row sets most widgets use: the user's brand, and the whole category. */
export function scoped(query: DashboardQuery) {
  const s = slice(query);
  const category = applyChips(s.current, query.selections);
  const categoryPrevious = s.previous ? applyChips(s.previous, query.selections) : null;
  return {
    ...s,
    category,
    categoryPrevious,
    own: own(category),
    ownPrevious: categoryPrevious ? own(categoryPrevious) : null,
  };
}

